"use client"

/**
 * 10 — Know where you stand today. Figma 336:1760.
 *
 * Measured off the frame (728 tall, content column at x=119):
 *
 *   photo     A woman sitting cross-legged on a plain terracotta backdrop,
 *             on the right, the left half clear for the copy (Eugene, 12.10;
 *             it replaced the man by the garden wall, his frosted blur ramp
 *             and the dark veil the busy photo needed). Same fictional woman
 *             as the fourth "Sound familiar?" card. Light film grain on top,
 *             the quiz banner's texture.
 *   rating    146   24px white/11 circle with the Google G, five 16px stars.
 *                   The "5.0 (36) · 1000+ check-ups" line after them is gone
 *                   (Eugene, 18.09: logo and stars only)
 *   heading   194   532 wide, two lines; 54px in the file → display-md 48
 *   features  335   check / health_and_safety / CalendarCheck + 14/20 Medium,
 *                   20 apart
 *   price     427   40 Medium + 24 Light, the baseline card's pair (dropped
 *                   on 18.09: the quiz is the primary here, not booking)
 *   buttons   493   290×52 white pill; 176×52 glass pill, 16 apart
 *
 * The column is centred in the 728 rather than hung at Figma's 146 (Eugene,
 * 29.09), since the lede made it taller than the frame's.
 *
 * The frame indents this column to 119; every other section here sits on
 * the 76px content column, so this one does too — 43px is the only thing
 * that differs.
 */

import { type ReactNode } from "react"
import { blobMediaUrl } from "@/lib/blob-url"
import Image from "next/image"
import Link from "next/link"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { BookingCta, PRODUCT_IDS } from "@/components/ui/booking-cta"
import { Button, buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { StarIcon, GoogleIcon } from "@/components/ui/icons"
import {
  HealthAndSafetyIcon,
  CalendarCheckIcon,
  CheckPlainIcon,
} from "./home-v4-figma"
import { useHomeV4Copy } from "./home-v4-copy"
import { bookingFlowUrl } from "./home-v4-data"

/** 2.36:1, Eugene's cleaned-up take. Wider than the section at desktop
 *  width, so the cover crop trims plain backdrop off the left and the whole
 *  figure stays in. */
const CLOSE_BG = blobMediaUrl(
  "/images/marketing/home-v4/home-v4-close-blonde-clean-4k-20261013.webp"
)

/** The quiz banner's grain tile, at a quarter of its strength. */
const GRAIN = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='grain'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='3' seed='7' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Cpath fill='%23808080' filter='url(%23grain)' d='M0 0h256v256H0z'/%3E%3C/svg%3E")` // ds-color-ignore: grey noise tile, blended soft-light, not a UI colour

/** Each icon at the size the file gives it: the check is 17, the other two
 *  24 — one `size-6` on all three was stretching the check. */
const FEATURE_ICONS = {
  check: () => <CheckPlainIcon className="shrink-0 text-white" />,
  shield: () => <HealthAndSafetyIcon className="size-6 shrink-0 text-white" />,
  calendar: () => <CalendarCheckIcon className="size-6 shrink-0 text-white" />,
}

/** `lede` replaces the line under the heading on another page (/pricing). */
export function HomeV4Close({
  section,
  lede,
  trackingLocation = "dev/home-v4",
  book,
  results,
  productId,
  scan = false,
}: {
  section: string
  lede?: string
  /** The booking button's `cta_click` location. */
  trackingLocation?: string
  /** A page that sells one thing closes on it, not the quiz: its own
   *  heading and one booking button in place of both (the `/go/bloodtest`
   *  landing, Eugene, 07.10). `lede` still sets the line under it. */
  book?: { heading: { plain: string; accent: string }; cta: ReactNode }
  /** The results line, on a page whose results take another time than the
   *  blood test's: the MRI's 7 to 10 days (audit, 09.10). */
  results?: string
  /** A test page's own product, so its booking button opens the plan with
   *  that test picked rather than empty (audit, 09.10). */
  productId?: string | readonly string[]
  /** That product is a scan: the booking opens on the MRI tab. */
  scan?: boolean
}) {
  const lp = useLocalePath()
  const { CLOSE: HOME_CLOSE } = useHomeV4Copy()
  const withResults = results
    ? {
        ...HOME_CLOSE,
        features: HOME_CLOSE.features.map((f) =>
          f.icon === "calendar" ? { ...f, text: results } : f
        ),
      }
    : HOME_CLOSE
  const CLOSE = book ? { ...withResults, heading: book.heading } : withResults

  return (
    <section className="relative isolate overflow-hidden bg-neutral-950 py-10 text-white md:flex md:h-[728px] md:items-center md:py-0">
      {/* On a phone the copy fills the width, so it sits on the plain
          backdrop at the photo's left edge rather than on her. From the
          tablet width up, the photo box runs past the right edge as the
          window narrows, so she slides right, out from under the copy, and
          the section clips the overhang (Eugene, 13.10, like the hero). */}
      <div className="absolute inset-0" aria-hidden>
        <div className="absolute inset-y-0 left-0 w-full md:w-[calc(100%+min(360px,max(0px,(1600px-100vw)*0.5)))]">
          <Image
            src={CLOSE_BG}
            alt=""
            fill
            sizes="100vw"
            quality={90}
            className="object-cover object-[0%_50%] md:object-[100%_50%]"
          />
        </div>
        <div
          className="pointer-events-none absolute inset-0 opacity-25 mix-blend-soft-light"
          style={{ backgroundImage: GRAIN }}
        />
      </div>

      <div className={`${section} relative z-10 w-full`}>
        {/* Figma 336:3451 — the Google line, logo and stars only. */}
        <div
          className="flex items-center gap-2"
          role="img"
          aria-label="Google, 5 stars"
        >
          <span className="flex size-6 items-center justify-center rounded-full bg-white/11">
            <GoogleIcon width={16} height={16} />
          </span>
          <span className="flex gap-px" aria-hidden>
            {Array.from({ length: 5 }, (_, i) => (
              <StarIcon key={i} width={14} height={14} className="opacity-95" />
            ))}
          </span>
        </div>

        {/* The draft's close-alt (Eugene, 18.09): the reader who reached the
            bottom and still is not sure gets the decision aid, not the hero's
            button again. "Find out in 5 minutes", because the quiz has more
            than three questions. */}
        <h2 className="type-display-md-emphasized mt-6 max-w-[560px] text-white">
          {CLOSE.heading.plain}
          <span className="text-bright-green-400">{CLOSE.heading.accent}</span>
        </h2>
        {/* What the quiz gives you, as the quiz's recommend step does it: a
            membership tier from the answers, then the add-on tests and scans
            they point to (Eugene, 29.09; on 18.09 there was no lede here). */}
        <p className="type-body-xl mt-4 max-w-[520px] text-white/85">
          {lede ?? CLOSE.lede}
        </p>

        <ul className="mt-[29px] flex flex-wrap items-center gap-5">
          {CLOSE.features.map((f) => {
            const Icon = FEATURE_ICONS[f.icon]
            return (
              <li key={f.text} className="flex items-center gap-2">
                <Icon />
                <span className="type-body-md-emphasized text-white">
                  {f.text}
                </span>
              </li>
            )
          })}
        </ul>

        {/* The price pair (40 Medium / 24 Light) is gone with the booking
            button's demotion; the baseline card and the hero carry it. */}

        {book ? (
          <div className="mt-[48px] flex w-full sm:w-auto">{book.cta}</div>
        ) : (
          <div className="mt-[48px] flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
            {/* Primary: the quiz. The internal `/quiz-focus` beat the external
              one in the experiment, so it is the route here. */}
            <Link
              href={lp("/quiz-focus")}
              className={cn(
                buttonVariants({ variant: "primary", size: "lg" }),
                "w-full bg-white px-8 text-neutral-950 hover:bg-neutral-100 hover:shadow-none sm:w-[290px]"
              )}
            >
              {CLOSE.ctaPrimary}
            </Link>
            {/* Secondary: booking, in the hero's glass pill — white at 29% over
              a 2px blur. */}
            <BookingCta
              productId={productId ?? PRODUCT_IDS.bloodTest}
              category={scan ? "mri" : undefined}
              step="product"
              overrideHref={bookingFlowUrl(scan ? "mri" : "blood")}
              trackingLabel="home_v3_close_cta"
              trackingLocation={trackingLocation}
              className="block w-full sm:inline-block sm:w-auto"
            >
              <Button
                size="lg"
                variant="text"
                className="w-full bg-white/30 text-white backdrop-blur-[2px] hover:bg-white/40 sm:w-auto"
              >
                {CLOSE.ctaSecondary}
              </Button>
            </BookingCta>
          </div>
        )}
      </div>
    </section>
  )
}
